import * as React from 'react';
import { Link, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, ColumnActionsMode, FontSizes, IDetailsListProps, DetailsRow, mergeStyleSets } from '@fluentui/react';

import { EnvironmentCheckItem } from 'hooks/api';
import { Title } from 'components/common/Title';
import { StepProps } from 'components/environmentcheck/EnvironmentCheck';
import { stepStyles as styles } from 'components/steps/commonStyles';
import { StepContainer } from 'components/common/steps/StepContainer';
import { useContext } from 'react';
import TranslationsContext from 'translations/TranslationsContext';
import translationsProvider from 'translations/translationsProvider';
import translationKeys from 'translationKeys';
import ThumbnailColumn from './resultsColumns/ThumbnailColumn';
import DetailsColumn from './resultsColumns/DetailsColumn';
import TitleColumn from './resultsColumns/TitleColumn';
import StatusColumn from './resultsColumns/StatusColumn';

export interface EnvironmentCheckItemToRender extends EnvironmentCheckItem {
	isChild?: boolean;
	titleKey?: string;
}

export const CheckResults = ({ environmentCheckResult, stepParams: { inStepError } }: StepProps) => {
	const { t } = useContext(TranslationsContext)!;
	const style = React.useMemo(() => mergeStyleSets({
		table: {
			paddingBottom: inStepError ? '8rem' : 'initial'
		}
	}), [inStepError]);

	const onRenderItemColumn = (item: EnvironmentCheckItemToRender, index: number, column: IColumn): JSX.Element | string | number => {

		if (column.key === 'thumbnail') {
			return <ThumbnailColumn item={item} />;
		};

		if (column.key === 'title') {
			return <TitleColumn item={item} />;
		}

		if (column.key === 'detail') {
			return <DetailsColumn item={item} />;
		}

		if (column.key === 'status') {
			return <StatusColumn status={item.status} />;
		}

		return <span>{item[column.key]}</span>;
	};

	const onRenderRow: IDetailsListProps['onRenderRow'] = props => <DetailsRow {...props} styles={{ root: { fontSize: FontSizes.size14, lineHeight: FontSizes.size20 }}} />;

	const flatternList = (checkResult: {[key: string]: EnvironmentCheckItem}, isChild?: boolean): EnvironmentCheckItem[] => {
		let sortedList: EnvironmentCheckItemToRender[] = Object.entries(checkResult || {}).map(([key, value]) => ({ ...value, titleKey: key }));

		if (isChild) {
			sortedList.forEach((item) => {
				item.isChild = true;
			});
		}

		sortedList.forEach((item, index) => {
			if (item.children && Object.keys(item.children)?.length) {
				sortedList = [
					...sortedList.slice(0, index + 1),
					...flatternList(item?.children, true),
					...sortedList.slice(index + 1),
				];
			};
		});

		return sortedList;
	};

	return (
		<StepContainer className={styles.root}>
			<Title>{t(translationKeys.CheckResult.StepContainer.Title)}</Title>
			<p className={styles.formElement}>{t(translationKeys.CheckResult.StepContainer.Paragraph.LeadingText)} <Link underline={true} target="_blank" href="https://aka.ms/mc4n/installer/docs/">{t(translationKeys.CheckResult.StepContainer.InstallerDocs.LinkText)}</Link> {t(translationKeys.CheckResult.StepContainer.Paragraph.OrText)} <Link underline={true} target="_blank" href="https://admin.powerplatform.microsoft.com/support/">{t(translationKeys.CheckResult.StepContainer.ContactSupport.LinkText)}</Link> {t(translationKeys.CheckResult.StepContainer.Paragraph.TrailingText)}</p>
			<div className={style.table}>
				<DetailsList
					items={flatternList(environmentCheckResult.result as unknown as  {[key: string]: EnvironmentCheckItem})}
					columns={columns}
					isSelectedOnFocus={false}
					setKey="set"
					selectionMode={SelectionMode.none}
					layoutMode={DetailsListLayoutMode.justified}
					selectionPreservedOnEmptyClick={false}
					onRenderItemColumn={onRenderItemColumn}
					onRenderRow={onRenderRow}
					styles={{ root: { marginTop: '-1rem' } }}
				/>
			</div>
		</StepContainer>
	);
};
const columns: IColumn[] = [
	{ key: 'thumbnail', name: '', ariaLabel: translationsProvider.staticTranslate(translationKeys.CheckResult.Columns.Status.AriaLabel), minWidth: 20, maxWidth: 20, columnActionsMode: ColumnActionsMode.disabled },
	{ key: 'title', name: translationsProvider.staticTranslate(translationKeys.CheckResult.Columns.Title.Name), fieldName: 'title', isResizable: false, minWidth: 200, maxWidth: 250, isMultiline: true, columnActionsMode: ColumnActionsMode.disabled },
	{ key: 'status', name: translationsProvider.staticTranslate(translationKeys.CheckResult.Columns.Status.Name), fieldName: 'status', isResizable: false, minWidth: 100, maxWidth: 200, isMultiline: true, columnActionsMode: ColumnActionsMode.disabled },
	{ key: 'detail', name: translationsProvider.staticTranslate(translationKeys.CheckResult.Columns.Details.Name), fieldName: 'detail', isResizable: false, minWidth: 100, maxWidth: 200, isMultiline: true, columnActionsMode: ColumnActionsMode.disabled }
];
